// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Conduit — one cell's line-work, as SVG path data in a 0..100 unit box.
//
// Pure string maths, no DOM, so a test can assert the geometry without a browser.
//
// ELBOWS ARE QUARTER-ARCS CENTRED ON THE CELL CORNER, which is what makes the plate read as
// continuous line-work rather than a mosaic of decorated squares: two elbows in adjacent cells meet
// at the boundary with the same tangent, so they form one unbroken S. A straight-line elbow would
// meet at an angle and every join would show a seam.
//
// THE TWO GAUGES DIFFER IN SHAPE, NOT ONLY COLOUR — culvert is a wide band with a lighter bore line
// down its middle, filament is a thin double hairline beaded at the centre. Printed in greyscale
// they are still instantly different, which is the real test of a colour-blind-safe scheme.

import { BLANK, CULVERT, DIRS, FILAMENT, live, type Dir, type Gauge, type Sides } from './pieces';

/** Cell box is 0..100 in both axes. The centre, and the midpoint of each side. */
const C = 50;
const EDGE: readonly [number, number][] = [
  [50, 0], // N
  [100, 50], // E
  [50, 100], // S
  [0, 50], // W
];

export const CULVERT_W = 17;
export const CULVERT_BORE = 5;
export const FILAMENT_GAP = 7;
export const FILAMENT_W = 3.2;

/** Which corner an elbow between two directions turns around. */
const cornerOf = (a: Dir, b: Dir): [number, number] => {
  const has = (d: Dir): boolean => a === d || b === d;
  const x = has(3) ? 0 : has(1) ? 100 : 50;
  const y = has(0) ? 0 : has(2) ? 100 : 50;
  return [x, y];
};

/** The centre-line path for a run between two sides of a cell, or from a side to the centre. */
export function centreLine(a: Dir, b: Dir | null): string {
  const [ax, ay] = EDGE[a];
  if (b === null) return `M ${ax} ${ay} L ${C} ${C}`;
  // Opposite sides: a straight line through the middle.
  if ((a + 2) % 4 === b) return `M ${ax} ${ay} L ${EDGE[b][0]} ${EDGE[b][1]}`;
  // Adjacent sides: a quarter-arc about the shared corner, radius 50, so it meets each boundary
  // perpendicular and joins seamlessly with whatever the neighbouring cell draws.
  const [cx, cy] = cornerOf(a, b);
  const [bx, by] = EDGE[b];
  const sweep = sweepOf(a, b);
  return `M ${ax} ${ay} A 50 50 0 0 ${sweep} ${bx} ${by}` + (cx === cy ? '' : '');
};

/** Arc direction, so the curve bulges toward the corner rather than away from it. */
function sweepOf(a: Dir, b: Dir): 0 | 1 {
  // Clockwise pairs (N->E, E->S, S->W, W->N) sweep one way; the reverse sweeps the other.
  return (b - a + 4) % 4 === 1 ? 0 : 1;
}

export interface CellPaths {
  /** Wide bands for culvert, drawn first. */
  culvertBand: string[];
  /** Lighter bore lines down the middle of each culvert band. */
  culvertBore: string[];
  /** Filament hairline pairs. */
  filament: string[];
  /** Centre bead for filament, and the coupler node. */
  bead: [number, number] | null;
  /** True when this cell couples the two gauges. */
  coupler: boolean;
}

/**
 * Turn a side tuple into drawable geometry.
 *
 * Every live side is connected to every other live side OF THE SAME GAUGE through the centre, except
 * that the bypass carries its two axes straight past each other. Rendering follows the same rule the
 * scorer uses, so what you see is what the graph is — a renderer that drew a join the scorer did not
 * make would be the worst possible bug in a connection game.
 */
export function pathsFor(sides: Sides, isCoupler: boolean, isBypass: boolean): CellPaths {
  const out: CellPaths = { culvertBand: [], culvertBore: [], filament: [], bead: null, coupler: isCoupler };

  const of = (g: Gauge): Dir[] => DIRS.filter((d) => sides[d] === g);
  const cs = of(CULVERT);
  const fs = of(FILAMENT);

  const runs = (ds: Dir[], push: (d: string) => void): void => {
    if (ds.length === 0) return;
    if (ds.length === 1) {
      push(centreLine(ds[0], null));
      return;
    }
    if (ds.length === 2) {
      push(centreLine(ds[0], ds[1]));
      return;
    }
    // A tee (or more): spokes from the centre, so the junction is visibly a junction.
    for (const d of ds) push(centreLine(d, null));
  };

  if (isBypass) {
    // Two independent straights that must NOT appear to meet. The filament is drawn broken either
    // side of the culvert band, the way a circuit diagram hops one wire over another.
    runs(cs, (d) => out.culvertBand.push(d));
    for (const d of out.culvertBand) out.culvertBore.push(d);
    for (const d of fs) out.filament.push(centreLine(d, null));
    return out;
  }

  runs(cs, (d) => out.culvertBand.push(d));
  for (const d of out.culvertBand) out.culvertBore.push(d);
  runs(fs, (d) => out.filament.push(d));
  if (fs.length > 0) out.bead = [C, C];
  return out;
}

/** Whether a cell should render the coupler's octagonal node. */
export const hasBothGauges = (s: Sides): boolean =>
  DIRS.some((d) => s[d] === CULVERT) && DIRS.some((d) => s[d] === FILAMENT);

export { BLANK, live };
